import { Injectable, inject, effect, untracked, NgZone } from '@angular/core'
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr'
import { take } from 'rxjs'
import { AuthService } from './auth.service'
import { AlertsService } from './alerts.service'
import { AuthStore } from '../state/auth.store'
import { NotificationsStore } from '../state/notifications.store'
import { Notification, ConnectionStatus } from '@shared/models/notification/notification.models'
import { AlertDto, AlertSeverity } from '@shared/models/dto/api-dtos'
import { LoggerService } from './logger.service'
import { environment } from '@env/environment'
import { ToastrService } from 'ngx-toastr'

/**
 * SignalR Notification Service
 *
 * Manages real-time notifications via SignalR connection.
 * Handles connection lifecycle, authentication, and notification processing.
 *
 * Features:
 * - Automatic connection management
 * - JWT token authentication
 * - Reconnection logic with exponential backoff
 * - Notification parsing and storage
 * - Connection status tracking
 * - Error handling and logging
 */
@Injectable({ providedIn: 'root' })
export class NotificationSignalRService {
  private readonly authService = inject(AuthService)
  private readonly alertsService = inject(AlertsService)
  private readonly authStore = inject(AuthStore)
  private readonly notificationsStore = inject(NotificationsStore)
  private readonly logger = inject(LoggerService)
  private readonly toastService = inject(ToastrService)
  private readonly ngZone = inject(NgZone)

  private hubConnection: HubConnection | null = null
  private reconnectAttempts = 0
  private readonly maxReconnectAttempts = 10
  private reconnectTimer?: ReturnType<typeof setTimeout>
  private connectionStatus: ConnectionStatus = ConnectionStatus.Disconnected
  private isInitialized = false
  /** True while a connect() call is in progress — prevents concurrent start() attempts */
  private _isConnecting = false
  /** Tracks the current user identifier (string for SSO, numeric string for regular) */
  private currentUserIdentifier: string | null = null

  // Reconnection configuration
  private readonly baseReconnectDelay = 15000 // 15 second
  private readonly maxReconnectDelay = 120000 // 120 seconds

  constructor() {
    this.logger.info('[NotificationSignalR] Service initialized')
    this.initialize()
  }

  /**
   * Initializes the notification service and sets up authentication monitoring.
   * Can be called publicly for explicit initialization (e.g., from APP_INITIALIZER).
   */
  initialize(): void {
    // If already initialized, just check if we need to connect
    if (this.isInitialized) {
      this.logger.info('[NotificationSignalR] Service already initialized, checking connection status...')
      if (this.authService.isAuthenticated() && !this.hubConnection) {
        const token = this.authService.getAccessToken()
        const userIdentifier = this.authStore.userIdentifier()
        if (token && userIdentifier) {
          this.logger.info('[NotificationSignalR] User already authenticated, connecting...', { userIdentifier })
          this.currentUserIdentifier = userIdentifier
          this.connect()
        }
      }
      return
    }

    this.logger.info('[NotificationSignalR] Initializing service...')

    // Mark as initialized SYNCHRONOUSLY before registering the effect so that any
    // subsequent call to initialize() (e.g. from APP_INITIALIZER) sees the flag and
    // returns early — preventing a second effect from being registered.
    this.isInitialized = true

    // Monitor authentication state and connect/disconnect accordingly
    effect(
      () => {
        const isAuthenticated = this.authStore.isAuthenticated()
        const userIdentifier = this.authStore.userIdentifier()
        // Read token without creating a reactive dependency: token refreshes must NOT
        // trigger a reconnect — the dynamic accessTokenFactory handles fresh tokens.
        const token = untracked(() => this.authStore.token())

        this.logger.info(`[NotificationSignalR] Authentication state changed`, {
          isAuthenticated,
          userIdentifier,
          currentUserIdentifier: this.currentUserIdentifier,
          hasToken: !!token,
          hasConnection: !!this.hubConnection,
          connectionState: this.hubConnection?.state || 'none',
        })

        // Check if user changed (different user logged in)
        const userChanged =
          this.currentUserIdentifier !== null &&
          this.currentUserIdentifier !== userIdentifier &&
          userIdentifier !== null

        // Handle user change — disconnect old, reconnect as new user
        if (userChanged && this.hubConnection) {
          this.logger.info('[NotificationSignalR] User changed, disconnecting...', {
            old: this.currentUserIdentifier,
            new: userIdentifier,
          })
          this.disconnect().then(() => {
            this.currentUserIdentifier = null
            if (isAuthenticated && token) {
              this.connect()
            }
          })
          return
        }

        // New authentication
        if (isAuthenticated && token && !this.hubConnection) {
          this.logger.info('[NotificationSignalR] User authenticated, initiating connection...', { userIdentifier })
          this.currentUserIdentifier = userIdentifier
          this.connect()
        }
        // Logout
        else if (!isAuthenticated && this.hubConnection) {
          this.logger.info('[NotificationSignalR] User logged out, disconnecting...')
          this.currentUserIdentifier = null
          this.disconnect()
        }
      },
      { allowSignalWrites: true }
    )
  }

  /**
   * Establishes SignalR connection to the notifications hub
   */
  async connect(): Promise<void> {
    this.logger.info('[NotificationSignalR] connect() called', {
      hasConnection: !!this.hubConnection,
      currentState: this.hubConnection?.state || 'none',
      connectionStatus: this.connectionStatus,
      reconnectAttempts: this.reconnectAttempts,
      isConnecting: this._isConnecting,
    })

    // Hard lock: prevents concurrent start() calls regardless of hubConnection state.
    // This covers the race where two callers both see hubConnection=null before the
    // first caller has set it (e.g. two effects firing in the same CD cycle).
    if (this._isConnecting) {
      this.logger.info('[NotificationSignalR] Connection attempt already in progress, skipping')
      return
    }

    // Check if already connected or connecting
    if (
      this.hubConnection &&
      (this.hubConnection.state === HubConnectionState.Connected ||
        this.hubConnection.state === HubConnectionState.Connecting)
    ) {
      this.logger.info('[NotificationSignalR] Connection already established or in progress', {
        state: this.hubConnection.state,
        connectionId: this.hubConnection.connectionId || 'none',
      })
      return
    }

    // Check authentication and token
    const token = this.authService.getAccessToken()
    const userIdentifier = this.authStore.userIdentifier()

    if (!token) {
      this.logger.warn('[NotificationSignalR] Cannot connect: No authentication token available', {
        userIdentifier,
        isAuthenticated: this.authService.isAuthenticated(),
      })
      this.notificationsStore.setConnectionStatus(false, false, 'No authentication token')
      this.currentUserIdentifier = null
      return
    }

    if (!userIdentifier) {
      this.logger.warn('[NotificationSignalR] Cannot connect: No user identifier available', {
        hasToken: !!token,
      })
      this.notificationsStore.setConnectionStatus(false, false, 'No user identifier')
      return
    }

    // Update current user if changed
    if (this.currentUserIdentifier !== userIdentifier) {
      this.logger.info('[NotificationSignalR] User identifier updated', {
        old: this.currentUserIdentifier,
        new: userIdentifier,
      })
      this.currentUserIdentifier = userIdentifier
    }

    // Log token info (without exposing the actual token)
    const tokenPreview = token.substring(0, 20) + '...'
    this.logger.info('[NotificationSignalR] Authentication token available', {
      tokenLength: token.length,
      tokenPreview,
    })

    try {
      this._isConnecting = true
      this.logger.info('[NotificationSignalR] Starting connection process...')
      this.notificationsStore.setConnectionStatus(false, true, null)
      this.connectionStatus = ConnectionStatus.Connecting

      // Build hub connection with authentication
      const hubUrl = this.getHubUrl()
      this.logger.info('[NotificationSignalR] Building hub connection', {
        hubUrl,
        baseUrl: environment.baseUrl,
        reconnectAttempts: this.reconnectAttempts,
      })

      this.hubConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          // Keep WebSockets as preferred transport but allow the negotiate round-trip so
          // Azure infrastructure properly tracks the connection and keepalives work.
          // skipNegotiation must NOT be set when keepalives are needed (it bypasses Azure
          // connection management and causes "Server timeout elapsed" after 30 s).
          transport: HttpTransportType.WebSockets,
          accessTokenFactory: () => {
            // Always return the latest token so auto-reconnects use a fresh JWT
            const freshToken = this.authService.getAccessToken()
            this.logger.debug('[NotificationSignalR] Access token factory called', { tokenLength: freshToken?.length })
            return freshToken ?? ''
          },
        })
        // Give the server 90 s to send a keepalive or real message before the client
        // considers the connection dead (default is 30 s, too short for Azure infra).
        .withServerTimeout(90000)
        // Send a client-side ping every 30 s to prevent proxy/firewall idle timeouts.
        .withKeepAliveInterval(30000)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
              const base = Math.min(
                this.baseReconnectDelay * Math.pow(2, retryContext.previousRetryCount),
                this.maxReconnectDelay
              )
              // Add ±20 % jitter to spread client reconnects and reduce thundering-herd
              const jitter = base * 0.2 * (Math.random() * 2 - 1)
              const delay = Math.round(base + jitter)
              this.logger.info(`[NotificationSignalR] Automatic reconnect scheduled`, {
                attempt: retryContext.previousRetryCount + 1,
                delay,
                maxAttempts: this.maxReconnectAttempts,
              })
              return delay
            }
            this.logger.warn('[NotificationSignalR] Max automatic reconnect attempts reached')
            return null // Stop reconnecting
          },
        })
        .configureLogging(environment.production ? LogLevel.Error : LogLevel.Information)
        .build()

      this.logger.info('[NotificationSignalR] Hub connection builder created')

      // Register event handlers
      this.registerHandlers()
      this.logger.info('[NotificationSignalR] Event handlers registered')

      // Start connection
      this.logger.info('[NotificationSignalR] Starting hub connection...')
      const startTime = Date.now()
      await this.hubConnection.start()
      const connectionTime = Date.now() - startTime

      this.connectionStatus = ConnectionStatus.Connected
      this.reconnectAttempts = 0
      this.notificationsStore.setConnectionStatus(true, false, null)

      this.logger.info('[NotificationSignalR] Connection established successfully', {
        connectionId: this.hubConnection.connectionId,
        connectionTime: `${connectionTime}ms`,
        state: this.hubConnection.state,
        userId: this.currentUserIdentifier,
        hubUrl,
      })

      // Clear any existing reconnect timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = undefined
        this.logger.debug('[NotificationSignalR] Cleared existing reconnect timer')
      }
    } catch (error) {
      this.connectionStatus = ConnectionStatus.Disconnected
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const errorDetails =
        error instanceof Error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack?.substring(0, 200),
            }
          : { error }

      // Detect HTTP 401 / 429 to avoid hammering the server
      const isUnauthorized = errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized')
      const isRateLimited = errorMessage.includes('429') || errorMessage.toLowerCase().includes('too many')

      this.notificationsStore.setConnectionStatus(false, false, errorMessage)
      this.logger.error('[NotificationSignalR] Failed to establish connection', {
        error: errorDetails,
        reconnectAttempts: this.reconnectAttempts,
        isUnauthorized,
        isRateLimited,
        willRetry: !isUnauthorized && this.reconnectAttempts < this.maxReconnectAttempts,
      })

      if (isUnauthorized) {
        this.logger.warn('[NotificationSignalR] 401 Unauthorized — logging out')
        this.hubConnection = null
        this._isConnecting = false
        this.authService.logout(true).subscribe()
        return
      }

      if (isRateLimited) {
        // 429 — back off for maxReconnectDelay (120 s) regardless of attempt count
        this.logger.warn('[NotificationSignalR] 429 Rate limited — applying maximum backoff before retry')
        this.reconnectAttempts = Math.max(this.reconnectAttempts, this.maxReconnectAttempts - 1)
      }

      // Schedule reconnection attempt
      this.scheduleReconnect()
    } finally {
      // Always release the lock so future connect() calls are not permanently blocked
      this._isConnecting = false
    }
  }

  /**
   * Disconnects from SignalR hub
   */
  async disconnect(): Promise<void> {
    this.logger.info('[NotificationSignalR] disconnect() called', {
      hasConnection: !!this.hubConnection,
      currentState: this.hubConnection?.state || 'none',
      hasReconnectTimer: !!this.reconnectTimer,
    })

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
      this.logger.debug('[NotificationSignalR] Cleared reconnect timer')
    }

    if (this.hubConnection) {
      try {
        this.connectionStatus = ConnectionStatus.Disconnecting
        const connectionId = this.hubConnection.connectionId
        this.logger.info('[NotificationSignalR] Stopping connection...', {
          connectionId: connectionId || 'none',
          state: this.hubConnection.state,
        })

        await this.hubConnection.stop()

        this.logger.info('[NotificationSignalR] Connection closed successfully', {
          connectionId: connectionId || 'none',
        })
      } catch (error) {
        this.logger.error('[NotificationSignalR] Error disconnecting', {
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                }
              : error,
        })
      } finally {
        this.hubConnection = null
        this.connectionStatus = ConnectionStatus.Disconnected
        this.notificationsStore.setConnectionStatus(false, false, null)
        // Don't clear currentUserIdentifier here — cleared on logout or user change
        this.logger.info('[NotificationSignalR] Disconnection complete', {
          userIdentifier: this.currentUserIdentifier,
        })
      }
    } else {
      this.logger.debug('[NotificationSignalR] No active connection to disconnect', {
        userIdentifier: this.currentUserIdentifier,
      })
    }
  }

  /**
   * Registers SignalR event handlers
   */
  private registerHandlers(): void {
    if (!this.hubConnection) {
      this.logger.warn('[NotificationSignalR] Cannot register handlers: No hub connection')
      return
    }

    this.logger.info('[NotificationSignalR] Registering event handlers...')

    // Handle incoming notifications
    this.hubConnection.on('ReceiveNotification', (alertDto: AlertDto) => {
      this.ngZone.run(() => {
        this.logger.debug('[NotificationSignalR] ReceiveNotification event received', {
          notificationId: alertDto.id,
          title: alertDto.title,
          category: alertDto.category,
          severity: alertDto.severity,
        })
        this.handleNotification(alertDto)
      })
    })

    // Handle connection closed
    this.hubConnection.onclose(error => {
      this.connectionStatus = ConnectionStatus.Disconnected
      const errorMessage = error?.message || 'Connection closed'
      this.notificationsStore.setConnectionStatus(false, false, errorMessage)

      this.logger.warn('[NotificationSignalR] Connection closed', {
        error: error
          ? {
              message: error.message,
              name: error.name,
            }
          : null,
        wasClean: !error,
        isAuthenticated: this.authService.isAuthenticated(),
      })

      // Attempt to reconnect if authenticated and same user
      const isAuthenticated = this.authService.isAuthenticated()
      const currentUserIdentifier = this.authStore.userIdentifier()

      if (isAuthenticated && currentUserIdentifier === this.currentUserIdentifier) {
        this.logger.info('[NotificationSignalR] All auto-reconnects exhausted. Starting manual reconnect cycle.', {
          userIdentifier: this.currentUserIdentifier,
        })
        // withAutomaticReconnect exhausted all its attempts — null the connection so
        // connect() can build a fresh HubConnection, then start the manual backoff cycle.
        this.hubConnection = null
        this.reconnectAttempts = 0
        this.scheduleReconnect()
      } else {
        this.logger.info('[NotificationSignalR] Not reconnecting', {
          isAuthenticated,
          currentUserIdentifier,
          storedIdentifier: this.currentUserIdentifier,
          reason: currentUserIdentifier !== this.currentUserIdentifier ? 'User changed' : 'Not authenticated',
        })
        if (currentUserIdentifier !== this.currentUserIdentifier) {
          this.currentUserIdentifier = null
        }
      }
    })

    // Handle reconnecting
    this.hubConnection.onreconnecting(error => {
      this.connectionStatus = ConnectionStatus.Reconnecting
      const errorMessage = error?.message || 'Reconnecting...'
      this.notificationsStore.setConnectionStatus(false, true, errorMessage)

      this.logger.info('[NotificationSignalR] Reconnecting...', {
        error: error
          ? {
              message: error.message,
              name: error.name,
            }
          : null,
        reconnectAttempts: this.reconnectAttempts,
      })
    })

    // Handle reconnected
    this.hubConnection.onreconnected(connectionId => {
      this.connectionStatus = ConnectionStatus.Connected
      this.reconnectAttempts = 0
      this.notificationsStore.setConnectionStatus(true, false, null)

      this.logger.info('[NotificationSignalR] Reconnected successfully', {
        connectionId,
        state: this.hubConnection?.state,
        reconnectAttempts: this.reconnectAttempts,
      })

      // Reload notifications to recover any missed during the disconnect window
      this.alertsService
        .loadInitialNotifications()
        .pipe(take(1))
        .subscribe({
          error: err => this.logger.warn('[NotificationSignalR] Failed to reload notifications after reconnect', err),
        })
    })

    this.logger.info('[NotificationSignalR] Event handlers registered successfully')
  }

  /**
   * Handles incoming notification from SignalR
   * @param alertDto Alert DTO from server
   */
  private handleNotification(alertDto: AlertDto): void {
    this.logger.info('[NotificationSignalR] Processing incoming notification', {
      id: alertDto.id,
      title: alertDto.title,
      message: alertDto.message,
      category: alertDto.category,
      severity: alertDto.severity,
      type: alertDto.type,
      successionFileId: alertDto.successionFileId,
      hasPayload: !!alertDto.payload,
      createdAt: alertDto.createdAt,
    })

    try {
      // Parse payload if present
      let parsedPayload: Record<string, unknown> | undefined
      if (alertDto.payload) {
        try {
          parsedPayload = JSON.parse(alertDto.payload)
          this.logger.debug('[NotificationSignalR] Payload parsed successfully', {
            payloadKeys: parsedPayload ? Object.keys(parsedPayload) : [],
          })
        } catch (error) {
          this.logger.warn('[NotificationSignalR] Failed to parse notification payload', {
            error: error instanceof Error ? error.message : error,
            payload: alertDto.payload.substring(0, 100) + '...',
          })
        }
      }

      // Create notification object
      const notification: Notification = {
        ...alertDto,
        parsedPayload,
        isProcessing: false,
      }

      // Add to store — returns false if duplicate (backend sends via User + Group)
      const isNew = this.notificationsStore.addNotification(notification)

      const unreadCount = this.notificationsStore.unreadCountSignal()
      this.logger.info('[NotificationSignalR] Notification added to store', {
        notificationId: notification.id,
        isNew,
        unreadCount,
        totalNotifications: this.notificationsStore.notificationsSignal().length,
      })

      // Only show toast and browser notification for new (non-duplicate) notifications
      if (isNew) {
        this.showInAppToast(notification)
        this.showBrowserNotification(notification)
      }

      this.logger.info('[NotificationSignalR] Notification processed successfully', {
        id: notification.id,
        title: notification.title,
        unreadCount,
      })
    } catch (error) {
      this.logger.error('[NotificationSignalR] Error handling notification', {
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack?.substring(0, 200),
              }
            : error,
        alertDto: {
          id: alertDto.id,
          title: alertDto.title,
        },
      })
    }
  }

  /**
   * Shows an in-app toast using ngx-toastr (already configured in app.config.ts).
   */
  private showInAppToast(notification: Notification): void {
    const title = notification.title || 'Nouvelle notification'
    const message = notification.message || ''

    switch (notification.severity) {
      case AlertSeverity.Info:
        this.toastService.info(message, title)
        break
      case AlertSeverity.Warning:
        this.toastService.warning(message, title)
        break
      case AlertSeverity.Critical:
        this.toastService.error(message, title)
        break
      default:
        this.toastService.info(message, title)
        break
    }
  }

  /**
   * Shows a browser (OS-level) notification when permission is granted.
   * @param notification Notification to show
   */
  private showBrowserNotification(notification: Notification): void {
    if (!('Notification' in window)) {
      return
    }

    if (Notification.permission === 'granted') {
      new Notification(notification.title || 'Nouvelle notification', {
        body: notification.message || '',
        icon: '/favicon.ico',
        tag: notification.id, // deduplicates identical notifications
      })
    } else if (Notification.permission !== 'denied') {
      // Request permission once; the notification itself will be shown next time
      Notification.requestPermission()
    }
  }

  /**
   * Schedules a reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.logger.error('[NotificationSignalR] Max reconnection attempts reached', {
        maxAttempts: this.maxReconnectAttempts,
        currentAttempts: this.reconnectAttempts,
      })
      this.notificationsStore.setConnectionStatus(false, false, 'Max reconnection attempts reached')
      return
    }

    const base = Math.min(this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay)
    const jitter = base * 0.2 * (Math.random() * 2 - 1)
    const delay = Math.round(base + jitter)

    this.reconnectAttempts++
    this.logger.info('[NotificationSignalR] Scheduling reconnection attempt', {
      attempt: this.reconnectAttempts,
      delay: `${delay}ms`,
      maxAttempts: this.maxReconnectAttempts,
      isAuthenticated: this.authService.isAuthenticated(),
    })

    this.reconnectTimer = setTimeout(() => {
      this.logger.info('[NotificationSignalR] Executing scheduled reconnection attempt', {
        attempt: this.reconnectAttempts,
      })
      this.connect()
    }, delay)
  }

  /**
   * Gets the SignalR hub URL (no token — accessTokenFactory handles auth)
   * @returns Hub URL
   */
  private getHubUrl(): string {
    const baseUrl = environment.baseUrl.replace('/api', '')
    const hubUrl = `${baseUrl}/hubs/notifications`

    this.logger.debug('[NotificationSignalR] Hub URL constructed', { baseUrl, hubUrl })

    return hubUrl
  }

  /**
   * Gets current connection status
   * @returns Connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus
  }

  /**
   * Checks if connected
   * @returns Whether connected
   */
  isConnected(): boolean {
    return (
      this.hubConnection?.state === HubConnectionState.Connected && this.connectionStatus === ConnectionStatus.Connected
    )
  }

  /**
   * Checks if a connection attempt is currently in progress (Connecting phase).
   * Use this alongside isConnected() to avoid triggering duplicate connect() calls.
   */
  isConnecting(): boolean {
    return this._isConnecting || this.hubConnection?.state === HubConnectionState.Connecting
  }

  /**
   * Manually triggers reconnection
   */
  async reconnect(): Promise<void> {
    this.logger.info('[NotificationSignalR] Manual reconnection triggered', {
      userIdentifier: this.currentUserIdentifier,
    })
    await this.disconnect()
    this.reconnectAttempts = 0
    await this.connect()
  }

  /**
   * Gets the current user identifier (string for SSO, numeric string for regular)
   */
  getCurrentUserId(): string | null {
    return this.currentUserIdentifier
  }

  /**
   * Checks if service is ready (initialized)
   * @returns Whether service is initialized
   */
  isReady(): boolean {
    return this.isInitialized
  }
}
